import React from 'react'

import { AuthUser, authApi, ModulePermission } from '@/services/authApi'

export type LoginPortal = 'admin' | 'user'

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  loading: boolean
  login: (username: string, password: string, portal: LoginPortal) => Promise<void>
  register: (payload: { username: string; password: string; full_name: string; department?: string; manager_name?: string }) => Promise<void>
  logout: () => void
  refreshMe: () => Promise<void>
  hasModulePermission: (module: ModulePermission) => boolean
  canViewProject: (projectId?: string | null) => boolean
  canViewStation: (stationId?: string | null) => boolean
  decisionRole: (projectId?: string | null) => 'viewer' | 'proposer' | 'approver' | undefined
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider(props: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null)
  const [token, setToken] = React.useState<string | null>(localStorage.getItem('cbms_token'))
  const [loading, setLoading] = React.useState(true)

  const logout = React.useCallback(() => {
    localStorage.removeItem('cbms_token')
    setToken(null)
    setUser(null)
  }, [])

  const refreshMe = React.useCallback(async () => {
    if (!localStorage.getItem('cbms_token')) {
      setUser(null)
      return
    }
    const resp: any = await authApi.me()
    setUser((resp?.data ?? resp) as AuthUser)
  }, [])

  React.useEffect(() => {
    ;(async () => {
      try {
        if (!token) {
          setUser(null)
          return
        }
        await refreshMe()
      } catch {
        logout()
      } finally {
        setLoading(false)
      }
    })()
  }, [token, refreshMe, logout])

  const login = React.useCallback(async (username: string, password: string, portal: LoginPortal) => {
    const resp: any = await authApi.login(username, password, portal)
    const payload = (resp?.data ?? resp) as { token: string; user: AuthUser }

    if (portal === 'admin' && !payload.user.is_admin) {
      throw new Error('当前账号不是管理员，请使用普通用户入口登录')
    }
    if (portal === 'user' && payload.user.is_admin) {
      throw new Error('管理员账号请从管理员入口登录')
    }

    localStorage.setItem('cbms_token', payload.token)
    setToken(payload.token)
    setUser(payload.user)
  }, [])

  const register = React.useCallback(async (payload: { username: string; password: string; full_name: string; department?: string; manager_name?: string }) => {
    await authApi.register({ ...payload, portal: 'user' })
  }, [])

  const hasModulePermission = React.useCallback(
    (module: ModulePermission) => {
      if (!user) return false
      if (user.is_admin) return true
      return (user.module_permissions || []).includes(module)
    },
    [user]
  )

  const canViewProject = React.useCallback(
    (projectId?: string | null) => {
      if (!user) return false
      if (user.is_admin) return true
      if (!projectId) return false
      return (user.project_permissions || []).includes(projectId)
    },
    [user]
  )

  const canViewStation = React.useCallback(
    (stationId?: string | null) => {
      if (!user) return false
      if (user.is_admin) return true
      if (!stationId) return false
      return (user.station_permissions || []).includes(stationId)
    },
    [user]
  )

  const decisionRole = React.useCallback(
    (projectId?: string | null) => {
      if (!user || !projectId) return undefined
      if (user.is_admin) return 'approver' as const
      return user.project_permission_grants?.find((item) => item.project_id === projectId)?.decision_role
    },
    [user],
  )

  const value: AuthContextValue = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshMe,
    hasModulePermission,
    canViewProject,
    canViewStation,
    decisionRole,
  }

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return ctx
}
